import { Injectable, computed, signal } from "@angular/core";
import {
  AuditInformation,
  AuditResponse,
  AuditSection,
  AuditSectionState,
  LabAuditState,
  LabPersonnel,
  RATING_POINTS,
} from "../models/lab-audit.models";
import { LAB_EQUIPMENT_1, LAB_EQUIPMENT_2, LAB_EQUIPMENT_3, EquipmentSheetDefinition } from "../data/lab-equipment";
import {
  CEMENT_JOB_QUESTIONS,
  LAB_TESTING_PROCEDURES_1_QUESTIONS,
  LAB_TESTING_PROCEDURES_2_QUESTIONS,
  SAFETY_QUESTIONS,
  SAMPLING_QUESTIONS,
} from "../data/lab-audit-questions";

const STORAGE_KEY = "cemsys.lab-audit-checklist.v1";
const MAX_SECTION_SCORE = 80;
const MAX_PERSONNEL = 5;

const LAB_EQUIPMENT_MAXIMUM = [LAB_EQUIPMENT_1, LAB_EQUIPMENT_2, LAB_EQUIPMENT_3]
  .flatMap(definition => definition.groups)
  .flatMap(group => group.rows)
  .filter(row => row.scored)
  .length * RATING_POINTS.G;

function equipmentResponseIds(definition: EquipmentSheetDefinition): string[] {
  return definition.groups.flatMap(group => group.rows.map(row => row.id));
}

const VALID_RESPONSE_IDS: Record<AuditSection, ReadonlySet<string>> = {
  safety: new Set(SAFETY_QUESTIONS.map(question => question.id)),
  cementJob: new Set(CEMENT_JOB_QUESTIONS.map(question => question.id)),
  labEquipment1: new Set(equipmentResponseIds(LAB_EQUIPMENT_1)),
  labEquipment2: new Set(equipmentResponseIds(LAB_EQUIPMENT_2)),
  labEquipment3: new Set(equipmentResponseIds(LAB_EQUIPMENT_3)),
  sampling: new Set(SAMPLING_QUESTIONS.map(question => question.id)),
  labTestingProcedures1: new Set(LAB_TESTING_PROCEDURES_1_QUESTIONS.map(question => question.id)),
  labTestingProcedures2: new Set(LAB_TESTING_PROCEDURES_2_QUESTIONS.map(question => question.id)),
};

function emptySection(additionalComments = ""): AuditSectionState {
  return { responses: {}, fields: {}, additionalComments };
}

function currentDate(): string {
  const now = new Date();
  const timezoneOffset = now.getTimezoneOffset() * 60_000;
  return new Date(now.getTime() - timezoneOffset).toISOString().slice(0, 10);
}

function createInitialState(): LabAuditState {
  return {
    information: {
      country: "",
      auditDateFrom: "",
      auditDateTo: "",
      reportDate: currentDate(),
      location: "",
      serviceCompany: "",
      auditors: "",
      laboratoryPersonnelCount: null,
      totalLabWorkingHours: null,
      workingHoursFrom: "",
      workingHoursTo: "",
    },
    personnel: [{ name: "", jobTitle: "", workExperience: "" }],
    safety: emptySection(),
    cementJob: emptySection(),
    labEquipment1: emptySection(),
    labEquipment2: emptySection(),
    labEquipment3: emptySection(),
    sampling: emptySection(),
    labTestingProcedures1: emptySection(),
    labTestingProcedures2: emptySection(),
  };
}

@Injectable({ providedIn: "root" })
export class LabAuditStateService {
  private readonly state = signal<LabAuditState>(this.loadState());

  readonly audit = this.state.asReadonly();
  readonly safetyScore = computed(() => this.calculateScore("safety"));
  readonly cementJobScore = computed(() => this.calculateScore("cementJob"));
  readonly labEquipmentScore = computed(() =>
    this.calculateScore("labEquipment1") +
    this.calculateScore("labEquipment2") +
    this.calculateScore("labEquipment3")
  );
  readonly samplingScore = computed(() => this.calculateScore("sampling"));
  readonly labTestingProceduresScore = computed(() =>
    this.calculateScore("labTestingProcedures1") +
    this.calculateScore("labTestingProcedures2")
  );
  readonly labTestingProcedures1Maximum = computed(() =>
    this.groupMaximum("labTestingProcedures1", "lab-test-slurry-") +
    this.groupMaximum("labTestingProcedures1", "lab-test-thickening-") +
    this.groupMaximum("labTestingProcedures1", "lab-test-fluid-loss-")
  );
  readonly labTestingProcedures2Maximum = computed(() =>
    this.groupMaximum("labTestingProcedures2", "lab-test-free-water-") +
    this.groupMaximum("labTestingProcedures2", "lab-test-crush-cubes-") +
    this.groupMaximum("labTestingProcedures2", "lab-test-rheology-")
  );
  readonly labTestingProceduresMaximum = computed(() =>
    this.labTestingProcedures1Maximum() + this.labTestingProcedures2Maximum()
  );
  readonly labEquipmentMaximum = LAB_EQUIPMENT_MAXIMUM;
  readonly safetyCompatibility = computed(() => this.safetyScore() / MAX_SECTION_SCORE);
  readonly cementJobCompatibility = computed(() => this.cementJobScore() / MAX_SECTION_SCORE);
  readonly labEquipmentCompatibility = computed(() => this.labEquipmentScore() / this.labEquipmentMaximum);
  readonly samplingCompatibility = computed(() => this.samplingScore() / 76);
  readonly labTestingProceduresCompatibility = computed(() => {
    const maximum = this.labTestingProceduresMaximum();
    return maximum ? this.labTestingProceduresScore() / maximum : null;
  });

  updateInformation<K extends keyof AuditInformation>(field: K, value: AuditInformation[K]): void {
    this.updateState(current => ({
      ...current,
      information: { ...current.information, [field]: value },
    }));
  }

  updateResponse(section: AuditSection, questionId: string, changes: Partial<AuditResponse>): void {
    this.updateState(current => {
      const sectionState = current[section];
      const existing = sectionState.responses[questionId] ?? this.emptyResponse();
      return {
        ...current,
        [section]: {
          ...sectionState,
          responses: {
            ...sectionState.responses,
            [questionId]: { ...existing, ...changes },
          },
        },
      };
    });
  }

  updateAdditionalComments(section: AuditSection, value: string): void {
    this.updateState(current => ({
      ...current,
      [section]: { ...current[section], additionalComments: value },
    }));
  }

  updateSectionField(section: AuditSection, field: string, value: string): void {
    this.updateState(current => ({
      ...current,
      [section]: {
        ...current[section],
        fields: { ...current[section].fields, [field]: value },
      },
    }));
  }

  resetAll(): void {
    this.state.set(createInitialState());
    this.persistState();
  }

  resetInformation(): void {
    const initialState = createInitialState();
    this.updateState(current => ({
      ...current,
      information: initialState.information,
    }));
  }

  resetSection(section: AuditSection): void {
    const initialState = createInitialState();
    this.updateState(current => ({
      ...current,
      [section]: initialState[section],
      ...(section === "safety" ? { personnel: initialState.personnel } : {}),
    }));
  }

  addPersonnel(): void {
    this.updateState(current => current.personnel.length >= MAX_PERSONNEL ? current : ({
        ...current,
        personnel: [...current.personnel, { name: "", jobTitle: "", workExperience: "" }],
      }));
  }

  updatePersonnel(index: number, field: keyof LabPersonnel, value: string): void {
    this.updateState(current => ({
      ...current,
      personnel: current.personnel.map((person, personIndex) =>
        personIndex === index ? { ...person, [field]: value } : person
      ),
    }));
  }

  removePersonnel(index: number): void {
    this.updateState(current => ({
      ...current,
      personnel: current.personnel.filter((_, personIndex) => personIndex !== index),
    }));
  }

  responseFor(section: AuditSectionState, questionId: string): AuditResponse {
    return section.responses[questionId] ?? this.emptyResponse();
  }

  scoreFor(section: AuditSection): number {
    return this.calculateScore(section);
  }

  private calculateScore(section: AuditSection): number {
    return Object.entries(this.state()[section].responses).reduce(
      (total, [questionId, response]) => total + (VALID_RESPONSE_IDS[section].has(questionId) && response.rating ? RATING_POINTS[response.rating] : 0),
      0
    );
  }

  private groupMaximum(section: AuditSection, questionPrefix: string): number {
    const hasRating = Object.entries(this.state()[section].responses)
      .some(([questionId, response]) => VALID_RESPONSE_IDS[section].has(questionId) && questionId.startsWith(questionPrefix) && response.rating !== "");
    const questionCount = Array.from(VALID_RESPONSE_IDS[section])
      .filter(questionId => questionId.startsWith(questionPrefix))
      .length;
    return hasRating ? questionCount * RATING_POINTS.G : 0;
  }

  private mergeSection(initial: AuditSectionState, saved: AuditSectionState | undefined, section: AuditSection): AuditSectionState {
    const responses = Object.fromEntries(
      Object.entries(saved?.responses ?? {}).filter(([questionId]) => VALID_RESPONSE_IDS[section].has(questionId))
    );
    return { ...initial, ...saved, responses };
  }

  private emptyResponse(): AuditResponse {
    return { answer: "", rating: "", comments: "" };
  }

  private updateState(updater: (current: LabAuditState) => LabAuditState): void {
    this.state.update(updater);
    this.persistState();
  }

  private persistState(): void {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(this.state()));
  }

  private loadState(): LabAuditState {
    const initialState = createInitialState();
    try {
      const savedState = localStorage.getItem(STORAGE_KEY);
      if (!savedState) {
        return initialState;
      }

      const parsed = JSON.parse(savedState) as Partial<LabAuditState>;
      return {
        ...initialState,
        ...parsed,
        information: { ...initialState.information, ...parsed.information },
        safety: this.mergeSection(initialState.safety, parsed.safety, "safety"),
        cementJob: this.mergeSection(initialState.cementJob, parsed.cementJob, "cementJob"),
        labEquipment1: this.mergeSection(initialState.labEquipment1, parsed.labEquipment1, "labEquipment1"),
        labEquipment2: this.mergeSection(initialState.labEquipment2, parsed.labEquipment2, "labEquipment2"),
        labEquipment3: this.mergeSection(initialState.labEquipment3, parsed.labEquipment3, "labEquipment3"),
        sampling: this.mergeSection(initialState.sampling, parsed.sampling, "sampling"),
        labTestingProcedures1: this.mergeSection(initialState.labTestingProcedures1, parsed.labTestingProcedures1, "labTestingProcedures1"),
        labTestingProcedures2: this.mergeSection(initialState.labTestingProcedures2, parsed.labTestingProcedures2, "labTestingProcedures2"),
        personnel: parsed.personnel?.length ? parsed.personnel.slice(0, MAX_PERSONNEL) : initialState.personnel,
      };
    } catch {
      return initialState;
    }
  }
}
